import { LocalStorageService } from "./local-storage-service";
import { getClientId } from "./client-identity";
import { beginRequest, endRequest, resetLoadingIndicator, LOADER_BAR, LOADER_BLOCKING } from "./loading-indicator";
import { toast } from "react-toastify";
import { t } from 'i18next'
import { getUserTimeZone, getUserTimeZoneOffset } from "../utils/date-utils";

/**
 * POST ile gidip aslında OKUMA olan uç noktalar (GetDataGrid, GetList, PreviewX…) ekranı
 * bloklamamalı; onlarda yalnız üstteki ince çubuk yanar. Eylem adı yolun 3. parçasıdır
 * (/api/Controller/Action/...) ve bu kod tabanında adlandırma tutarlıdır. Yanlış sınıflama
 * en fazla göstergenin BİÇİMİNİ değiştirir, isteğin kendisine dokunmaz.
 * Tek tek çağrılar `blocking` geçerek bu tahmini ezebilir.
 */
const READ_ACTION_PATTERN = /^(Get|List|DataGrid|Preview|Search|Download)/;

function isReadEndpoint(endpoint) {
    const action = (endpoint || '').split('?')[0].split('/')[3] || '';
    return READ_ACTION_PATTERN.test(action);
}

/**
 * Bu isteğin küresel göstergeyi nasıl yakacağı:
 *  null              → hiç gösterme (hideLoader / tarayıcı indirmesi; çağıran kendi göstergesini yönetiyor)
 *  LOADER_BAR        → yalnız üst çubuk, ekran serbest
 *  LOADER_BLOCKING   → çubuk + (uzarsa) karartma; tıklama kalkanı baştan konur
 */
function getLoaderWeight({ endpoint, method, hideLoader, browserDownload, blocking }) {
    if (hideLoader || browserDownload)
        return null;

    const shouldBlock = blocking ?? (method !== 'GET' && !isReadEndpoint(endpoint));
    return shouldBlock ? LOADER_BLOCKING : LOADER_BAR;
}

function setLogout(func) {
    document.logout = func;
}

function getAuthorization() {
    const token = LocalStorageService.getToken();
    return `Bearer ${token}`;
}

/**
 * Tarayıcı adres çubuğuna giden (indirme, yeni sekme, görüntüleyici) URL'lere eklenecek token
 * parametresi. Çerezli oturumda BOŞ döner: istek aynı origin'e gittiği için çerez zaten taşınır ve
 * token URL'de, yani geçmişte/log'larda görünmez. Elinde hâlâ eski token olan kullanıcıda ise
 * parametre eskisi gibi eklenir.
 */
function tokenQueryParam(prefix = '&') {
    const token = LocalStorageService.getToken();
    return token ? `${prefix}access_token=${encodeURIComponent(token)}` : '';
}

/**
 * Authorization header'ı YALNIZ token varken eklenir. Boş bir `Bearer ` göndermek çerezli oturumu
 * kırar: sunucu "Bearer ile geldi" deyip çereze hiç bakmaz ve istek 401 olur.
 */
function authHeaders() {
    const token = LocalStorageService.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

function getApi() {
    return window.AppSettings.server.api
}

async function request({ endpoint, method, body, onValidationError, contentType, isResponseFile, showSuccess, hideLoader, browserDownload, hideError, onReceivedBytes, blocking }) {
    // Gösterge sayaçlı ve `finally` ile kapatılıyor: paralel isteklerde ilk biten diğerlerinin
    // göstergesini söndürmez, hata/erken dönüş yollarında da sayaç açıkta kalmaz.
    const loaderWeight = getLoaderWeight({ endpoint, method, hideLoader, browserDownload, blocking });
    if (loaderWeight)
        beginRequest(loaderWeight);

    try {
        const token = LocalStorageService.getToken();

        let headers = {};

        let url = new URL(`${(new URL(window.location.href).origin)}${endpoint}`);
        if (getApi())
            url = new URL(`${getApi()}${endpoint}`);

        if (isResponseFile || browserDownload)
            url.searchParams.append('request_time', new Date().getTime().toString());

        if (browserDownload) {
            // Çerezli oturumda token yok; tarayıcının kendi isteği çerezi taşır.
            if (token) {
                url.searchParams.append('access_token', token);
            }
            const href = document.createElement('a');
            href.setAttribute('href', url)
            href.setAttribute('download', '')
            href.style.display = 'none'
            href.click();
            // window.open(url, '_blank')
            return
        }

        if (contentType) {
            headers['Content-Type'] = contentType;
        }

        // Cihaz kimliği: aynı IP+kullanıcıdaki farklı PC'leri loglarda ayırt etmek için her istekte gönderilir.
        headers['X-Client-Id'] = getClientId();

        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(url, {
            method: method,
            headers: headers,
            // Oturum çerezi her istekte gitsin. Aynı origin'de zaten giderdi; 'include' ayrıca
            // geliştirmede (localhost:3000 → API başka origin) da çalışmasını sağlıyor — sunucuda
            // CORS AllowCredentials + açık origin listesi mevcut.
            credentials: 'include',
            body: JSON.stringify(body)
        });

        if (response.status === 401) {
            if (document.logout) {
                // Oturum düştü: bekleyen isteklerin göstergesi giriş ekranında takılı kalmasın.
                resetLoadingIndicator()
                document.logout()
            }
            return {
                success: false,
            }
        }

        let json = {};
        if (isResponseFile && response.status === 200) {
            const content = response.headers.get('Content-Disposition');
            let fileName = null
            if (content) {
                fileName = content.split(';')[1].split('=')[1].replaceAll('"', '');
            }

            json = {
                fileName,
                success: true,
                data: URL.createObjectURL(await response.blob())
            }
        }
        else {
            json = await response.json();
        }

        if (response.status === 200) {
            if ((method === 'POST' || method === 'PUT') && showSuccess) {
                toast.success(t('Operation successful'));
            }

            return {
                success: true,
                ...json
            }
        } else if (response.status === 204) {
            json.message && toast.success(json.message);

            return {
                success: true,
                message: json.message
            }
        }
        else if (response.status >= 500) {
            // Gövde camelCase serileşiyor (AddNewtonsoftJson varsayılanı); eski PascalCase okuması yedekte.
            const message = json.message ?? json.Message;
            const errorData = json.errorData ?? json.ErrorData;

            if (!hideError) {
                message && toast.error(t(message));
            }

            return {
                success: false,
                message,
                errorData
            }
        }
    }
    catch (exp) {
        console.error(exp)

        if (!window.lastErrorTime)
            window.lastErrorTime = 0;

        if (!hideError && window.lastErrorTime < new Date().getTime() - 10000) {
            window.lastErrorTime = new Date().getTime();
            toast.error(t('Operation failed!'), 2000)
        }

        return {
            success: false,
            data: null,
            message: null
        }
    }
    finally {
        if (loaderWeight)
            endRequest(loaderWeight);
    }
}

function jsonRequest({ endpoint, body, method, onValidationError, isResponseFile, showSuccess, hideLoader, browserDownload, hideError, onReceivedBytes, blocking }) {
    return request({
        endpoint,
        body,
        method,
        onValidationError,
        isResponseFile,
        contentType: 'application/json',
        showSuccess,
        hideLoader,
        browserDownload,
        hideError,
        onReceivedBytes,
        blocking
    })
}

function formRequest({ endpoint, form, onValidationError }) {
    return new Promise((res, rej) => {
        const request = new XMLHttpRequest();
        request.open("POST", `${getApi()}${endpoint}`, true);

        // Çerezli oturumda header eklenmez; XHR aynı origin'de çerezi zaten taşır, cross-origin
        // (geliştirme) için withCredentials gerekir.
        request.withCredentials = true;
        const formAuth = authHeaders();
        if (formAuth.Authorization) {
            request.setRequestHeader('Authorization', formAuth.Authorization)
        }
        request.onreadystatechange = () => {
            if (request.readyState === 4 && request.status === 200) {
                res(JSON.parse(request.responseText))
            }
        };
        request.send(form);
    })
}

// Büyük dosyalar (JDK ~200 MB) için: yükleme ilerlemesi bildirir ve hatada reject eder.
function uploadRequest({ endpoint, form, onProgress }) {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('POST', `${getApi()}${endpoint}`, true);
        request.withCredentials = true;
        const uploadAuth = authHeaders();
        if (uploadAuth.Authorization) {
            request.setRequestHeader('Authorization', uploadAuth.Authorization);
        }

        request.upload.onprogress = (event) => {
            if (onProgress && event.lengthComputable) {
                onProgress(Math.round((event.loaded * 100) / event.total));
            }
        };

        request.onload = () => {
            if (request.status === 200) {
                resolve({ success: true, ...JSON.parse(request.responseText) });
                return;
            }

            let message = t('Upload failed');
            try {
                const body = JSON.parse(request.responseText);
                message = body?.Message || body?.message || message;
            } catch {
                // gövde JSON değilse varsayılan mesajla devam et
            }
            toast.error(message);
            reject(new Error(message));
        };

        request.onerror = () => {
            toast.error(t('Upload failed'));
            reject(new Error('network error'));
        };

        request.send(form);
    })
}

const Firm = {
    GetList: async function getFirmList() {
        return jsonRequest({
            endpoint: `/api/Firm/GetList`,
            method: 'GET'
        })
    },
}

const PacsSystem = {
    Create: async function getPacsSystemList(hospitalId, data) {
        return jsonRequest({
            endpoint: `/api/PacsSystem/Create/${hospitalId}`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    Edit: async function getPacsSystemList(hospitalId, pacsSystemId, data) {
        return jsonRequest({
            endpoint: `/api/PacsSystem/Edit/${hospitalId}/${pacsSystemId}`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    Get: async function getPacsSystem(hospitalId, pacsSystemId) {
        return jsonRequest({
            endpoint: `/api/PacsSystem/Get/${hospitalId}/${pacsSystemId}`,
            method: 'GET'
        })
    },
    GetList: async function getPacsSystemList(hospitalId) {
        return jsonRequest({
            endpoint: `/api/PacsSystem/GetList/${hospitalId}`,
            method: 'GET'
        })
    },
    GenerateClientCertificate: async function (hospitalId, pacsSystemId, data) {
        return jsonRequest({
            endpoint: `/api/PacsSystem/GenerateClientCertificate/${hospitalId}/${pacsSystemId}`,
            method: 'POST',
            body: data,
            isResponseFile: true
        })
    },
}

const Hospital = {
    Create: async function getHospitalList(data) {
        return jsonRequest({
            endpoint: `/api/Hospital/Create`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    Edit: async function getHospitalList(hospitalId, data) {
        return jsonRequest({
            endpoint: `/api/Hospital/Edit/${hospitalId}`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    GetList: async function () {
        return jsonRequest({
            endpoint: `/api/Hospital/GetList`,
            method: 'GET'
        })
    },
    GetDataGrid: async function getDataGrid(gridInput) {
        return jsonRequest({
            endpoint: `/api/Hospital/GetDataGrid`,
            method: 'POST',
            body: gridInput
        })
    },
    Get: async function getHospital(id) {
        return jsonRequest({
            endpoint: `/api/Hospital/Get/${id}`,
            method: 'GET'
        })
    },
}

const Modality = {
    GetList: async function () {
        return jsonRequest({
            endpoint: `/api/Modality/GetList`,
            method: 'GET'
        })
    }
}

const Device = {
    Create: async function (data) {
        return jsonRequest({
            endpoint: `/api/Device/Create`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    Update: async function (data) {
        return jsonRequest({
            endpoint: `/api/Device/Update`,
            method: 'PUT',
            body: data,
            showSuccess: true
        })
    },
    Delete: async function (deviceId) {
        return jsonRequest({
            endpoint: `/api/Device/Delete/${deviceId}`,
            method: 'DELETE',
            showSuccess: true
        })
    },
    GetById: async function (deviceId) {
        return jsonRequest({
            endpoint: `/api/Device/Get/${deviceId}`,
            method: 'GET'
        })
    },
    GetList: async function () {
        return jsonRequest({
            endpoint: `/api/Device/GetList`,
            method: 'GET'
        })
    },
    GetListByHospital: async function (hospitalId) {
        return jsonRequest({
            endpoint: `/api/Device/GetListByHospital/${hospitalId}`,
            method: 'GET'
        })
    },
    GetDataGrid: async function (gridInput) {
        return jsonRequest({
            endpoint: `/api/Device/GetDataGrid`,
            method: 'POST',
            body: gridInput
        })
    },
}

const WorklistItem = {
    Create: async function (data) {
        return jsonRequest({
            endpoint: `/api/WorklistItem/Create`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    Update: async function (data) {
        return jsonRequest({
            endpoint: `/api/WorklistItem/Update`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    Schedule: async function (data) {
        return jsonRequest({
            endpoint: `/api/WorklistItem/Schedule`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    BulkSchedule: async function (data) {
        return jsonRequest({
            endpoint: `/api/WorklistItem/BulkSchedule`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    AddToAppointment: async function (data) {
        return jsonRequest({
            endpoint: `/api/WorklistItem/AddToAppointment`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    ChangeStatus: async function (data) {
        return jsonRequest({
            endpoint: `/api/WorklistItem/ChangeStatus`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    Delete: async function (worklistItemId) {
        return jsonRequest({
            endpoint: `/api/WorklistItem/Delete/${worklistItemId}`,
            method: 'DELETE',
            showSuccess: true
        })
    },
    GetById: async function (worklistItemId) {
        return jsonRequest({
            endpoint: `/api/WorklistItem/GetById/${worklistItemId}`,
            method: 'GET'
        })
    },
    GetByStudyId: async function (studyId) {
        return jsonRequest({
            endpoint: `/api/WorklistItem/GetByStudyId/${studyId}`,
            method: 'GET'
        })
    },
    GetByDevice: async function (deviceId, startDate, endDate) {
        return jsonRequest({
            endpoint: `/api/WorklistItem/GetByDevice/${deviceId}?startDate=${startDate}&endDate=${endDate}`,
            method: 'GET'
        })
    },
    GetDataGrid: async function (gridInput) {
        return jsonRequest({
            endpoint: `/api/WorklistItem/GetDataGrid`,
            method: 'POST',
            body: gridInput
        })
    },
    SendToDevice: async function (worklistItemId) {
        return jsonRequest({
            endpoint: `/api/WorklistItem/SendToDevice/${worklistItemId}`,
            method: 'POST',
            showSuccess: true
        })
    },
}

const PermissionGroup = {
    GetList: async function () {
        return jsonRequest({
            endpoint: `/api/PermissionGroup/GetList`,
            method: 'GET'
        })
    },
}

const Role = {
    GetList: async function () {
        return jsonRequest({
            endpoint: `/api/Role/GetList`,
            method: 'GET'
        })
    },
    Get: async function (roleId) {
        return jsonRequest({
            endpoint: `/api/Role/Get/${roleId}`,
            method: 'GET'
        })
    },
    Create: async function (name) {
        return jsonRequest({
            endpoint: `/api/Role/Create`,
            method: 'POST',
            body: name,
            showSuccess: true
        })
    },
    Edit: async function (roleId, name) {
        return jsonRequest({
            endpoint: `/api/Role/Edit/${roleId}`,
            method: 'POST',
            body: name,
            showSuccess: true
        })
    },
    SetRolePermissions: async function (roleId, permissions) {
        return jsonRequest({
            endpoint: `/api/Role/SetRolePermissions/${roleId}`,
            method: 'POST',
            body: permissions,
            showSuccess: true
        })
    }
}

const ReportGenerator = {
    GetReportModel: async (studyId, requestId, reportId) => {
        return jsonRequest({
            endpoint: `/api/ReportGenerator/GetReportModel/${studyId}/${requestId}/${reportId}`,
            method: 'GET',
        });
    },
    GetReportVersionPreview: async (studyId, versionId) => {
        return jsonRequest({
            endpoint: `/api/ReportGenerator/GetReportVersionPreview/${studyId}/${versionId}`,
            method: 'GET',
        });
    },
    GetReportPdf: function (studyId, requestId, reportId) {
        return jsonRequest({
            endpoint: `/api/ReportGenerator/GetReportPdf/${studyId}/${requestId}/${reportId}`,
            method: 'GET',
            isResponseFile: true,
            showSuccess: true,
        });
    },
    GetReportPdfWithLanguage: function (studyId, requestId, reportId, language) {
        return jsonRequest({
            endpoint: `/api/ReportGenerator/GetReportPdfWithLanguage/${studyId}/${requestId}/${reportId}/language/${language}`,
            method: 'GET',
            isResponseFile: true,
            showSuccess: true,
        });
    },
    GetReportPreviewPdf: function (studyId, requestId, reportId, content) {
        return jsonRequest({
            endpoint: `/api/ReportGenerator/GetReportPreviewPdf/${studyId}/${requestId}/${reportId}`,
            method: 'POST',
            body: content
        });
    },
    GetReportWord: function (studyId, requestId, reportId) {
        return jsonRequest({
            endpoint: `/api/ReportGenerator/GetReportWord/${studyId}/${requestId}/${reportId}`,
            method: 'GET',
            isResponseFile: true,
            showSuccess: true,
        });
    },
    GetReportWordWithLanguage: function (studyId, requestId, reportId, language) {
        return jsonRequest({
            endpoint: `/api/ReportGenerator/GetReportWordWithLanguage/${studyId}/${requestId}/${reportId}/language/${language}`,
            method: 'GET',
            isResponseFile: true,
            showSuccess: true,
        });
    },
    CreatePdfZip: async function (studyIdList) {
        return jsonRequest({
            endpoint: `/api/ReportGenerator/CreatePdfZip`,
            method: 'POST',
            isResponseFile: true,
            body: studyIdList,
            showSuccess: true,
        });
    },
    CreateWordZip: async function (studyIdList) {
        return jsonRequest({
            endpoint: `/api/ReportGenerator/CreateWordZip`,
            method: 'POST',
            isResponseFile: true,
            body: studyIdList,
            showSuccess: true,
        });
    },
    CreatePdfZipByReportIdList: async function (reportIdList) {
        return jsonRequest({
            endpoint: `/api/ReportGenerator/CreatePdfZipByReportIdList`,
            method: 'POST',
            isResponseFile: true,
            body: reportIdList,
            showSuccess: true,
        });
    },
    CreateWordZipByReportIdList: async function (reportIdList) {
        return jsonRequest({
            endpoint: `/api/ReportGenerator/CreateWordZipByReportIdList`,
            method: 'POST',
            isResponseFile: true,
            body: reportIdList,
            showSuccess: true,
        });
    },
};

const Report = {
    CreateSignZip: async function (reportIdList) {
        return jsonRequest({
            endpoint: `/api/Report/CreateSignZip`,
            method: 'POST',
            isResponseFile: true,
            body: reportIdList,
            showSuccess: true
        })
    },
    RemoveSignature: async function (reportIdList) {
        return jsonRequest({
            endpoint: `/api/Report/RemoveSignature`,
            method: 'POST',
            body: reportIdList,
            showSuccess: true
        })
    },
    Get: async function (studyId, requestId, reportId) {
        return jsonRequest({
            endpoint: `/api/Report/Get/${studyId}/${requestId}/${reportId}`,
            method: 'GET'
        })
    },
    GetPreview: async function (studyId, requestId, reportId) {
        return jsonRequest({
            endpoint: `/api/Report/GetPreview/${studyId}/${requestId}/${reportId}`,
            method: 'GET'
        })
    },
    // silent: bkz. Study.DataGrid — ReportGrid göstergeyi tablonun içinde veriyor.
    GetDataGrid: async function (input, { silent } = {}) {
        return jsonRequest({
            endpoint: `/api/Report/GetDataGrid`,
            method: 'POST',
            body: input,
            hideLoader: silent
        })
    },
    // Sayaç ayrı istek: grid cevabı sayım beklemez. exact=false → sunucu 500'de keser ({count, isCapped}).
    // hideLoader: sayım sayfayı bloklamamalı — geri bildirim grid'in toplam alanındaki yerel spinner ile verilir.
    GetDataGridCount: async function (input, exact = false) {
        return jsonRequest({
            endpoint: `/api/Report/GetDataGridCount?exact=${exact}`,
            method: 'POST',
            body: input,
            hideLoader: true
        })
    },
    Create: async function (studyId, requestId, input) {
        return jsonRequest({
            endpoint: `/api/Report/Create/${studyId}/${requestId}`,
            method: 'POST',
            body: input,
            showSuccess: true
        })
    },
    Edit: async function (studyId, requestId, reportId, input) {
        return jsonRequest({
            endpoint: `/api/Report/Edit/${studyId}/${requestId}/${reportId}`,
            method: 'POST',
            body: input,
            showSuccess: true
        })
    },
    ConfirmReport: async function (studyId, requestId, reportId, input = {}) {
        return jsonRequest({
            endpoint: `/api/Report/ConfirmReport/${studyId}/${requestId}/${reportId}`,
            method: 'POST',
            body: input,
            showSuccess: true
        })
    },
    RemoveReportConfirmation: async function (studyId, requestId, reportId) {
        return jsonRequest({
            endpoint: `/api/Report/RemoveReportConfirmation/${studyId}/${requestId}/${reportId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    RemoveReport: async function (studyId, requestId) {
        return jsonRequest({
            endpoint: `/api/Report/RemoveReport/${studyId}/${requestId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    // Rapor kaydından bağımsız: içerik/onay/imza durumuna dokunmaz, onaylı raporda da çalışır.
    SetPathologicalFinding: async function (studyId, requestId, reportId, hasPathologicalFinding) {
        return jsonRequest({
            endpoint: `/api/Report/SetPathologicalFinding/${studyId}/${requestId}/${reportId}`,
            method: 'POST',
            body: { hasPathologicalFinding },
            showSuccess: true
        })
    },
    SetHbysTransfer: async function (studyId, requestId, reportId, isTransfer) {
        return jsonRequest({
            endpoint: `/api/Report/SetHbysTransfer/${studyId}/${requestId}/${reportId}`,
            method: 'POST',
            body: isTransfer
        })
    },
    GetReportFileData: function (studyId, requestId, reportId) {
        return jsonRequest({
            endpoint: `/api/Report/GetReportFileData/${studyId}/${requestId}/${reportId}`,
            method: 'GET',
        })
    },
    // Saat dilimi burada eklenir, çağıranın hatırlamasına gerek kalmasın diye: dosyadaki tarihler
    // sunucuda metne çevriliyor, bilgi gitmezse UTC yazılır (bkz. ReportExcelService).
    GetReportsExcel: (gridInput) => {
        const timeZone = getUserTimeZone();
        const query = [
            timeZone ? `timeZone=${encodeURIComponent(timeZone)}` : null,
            `timeZoneOffset=${getUserTimeZoneOffset()}`,
        ].filter(Boolean).join('&');

        return jsonRequest({
            endpoint: `/api/Report/GetReportsExcel?${query}`,
            method: 'POST',
            body: gridInput,
        })
    },
    DownloadReportsExcel: (notificationId) => {
        return jsonRequest({
            endpoint: `/api/Report/DownloadReportsExcel/${notificationId}`,
            method: 'POST',
            isResponseFile: true
        })
    },
    GetReportVersions: (studyId, reportId, gridInput) => {
        return jsonRequest({
            endpoint: `/api/Report/GetReportVersions/${studyId}/${reportId}`,
            method: 'POST',
            body: gridInput,
            isResponseFile: false
        })
    },
}

// Rapor Faturalandırma ekranı — kendi uç noktaları; Report modülüyle hiçbir çağrıyı paylaşmaz.
const ReportBilling = {
    GetDataGrid: async function (input) {
        return jsonRequest({
            endpoint: `/api/ReportBilling/GetDataGrid`,
            method: 'POST',
            body: input
        })
    },
    // Sayaç ayrı istek: exact=false → sunucu 500'de keser ({count, isCapped}).
    // hideLoader: sayım sayfayı bloklamamalı.
    GetDataGridCount: async function (input, exact = false) {
        return jsonRequest({
            endpoint: `/api/ReportBilling/GetDataGridCount?exact=${exact}`,
            method: 'POST',
            body: input,
            hideLoader: true
        })
    },
    GetExcel: async function (input) {
        return jsonRequest({
            endpoint: `/api/ReportBilling/GetExcel`,
            method: 'POST',
            body: input
        })
    },
    DownloadExcel: async function (notificationId) {
        return jsonRequest({
            endpoint: `/api/ReportBilling/DownloadExcel/${notificationId}`,
            method: 'POST',
            isResponseFile: true
        })
    },
    GetLogs: async function (reportId, gridInput) {
        return jsonRequest({
            endpoint: `/api/ReportBilling/GetLogs/${reportId}`,
            method: 'POST',
            body: gridInput
        })
    },
    GetBatchLogs: async function (batchId, gridInput) {
        return jsonRequest({
            endpoint: `/api/ReportBilling/GetBatchLogs/${batchId}`,
            method: 'POST',
            body: gridInput
        })
    },
    // items: [{reportId, expectedStatus}] — expectedStatus istemcinin GÖRDÜĞÜ durumdur; sunucu
    // yalnız hâlâ o durumda olanları değiştirir, gerisini skipped olarak döner.
    UpdateStatus: async function (input) {
        return jsonRequest({
            endpoint: `/api/ReportBilling/UpdateStatus`,
            method: 'POST',
            body: input
        })
    },
    UpdateStatusByFilter: async function (input) {
        return jsonRequest({
            endpoint: `/api/ReportBilling/UpdateStatusByFilter`,
            method: 'POST',
            body: input
        })
    },
    // Mükerrer tespiti: sayfalama GRUP bazında. Sunucu ağır daraltmayı tek küme-tabanlı sorguda
    // yapar, zaman yakınlığı gruplamasını daralmış küme üzerinde çalıştırır.
    DetectDuplicates: async function (input) {
        return jsonRequest({
            endpoint: `/api/ReportBilling/DetectDuplicates`,
            method: 'POST',
            body: input
        })
    },
    // Hedef durum (Mükerrer) ve kaynak sunucuda belirlenir; istemci yalnız hangi raporlar ve
    // onları hangi durumda gördüğünü (expectedStatus) gönderir.
    MarkDuplicates: async function (input) {
        return jsonRequest({
            endpoint: `/api/ReportBilling/MarkDuplicates`,
            method: 'POST',
            body: input
        })
    },
    // Yalnız rapor id'leri gider; istem/çekim eşleşmesini ve "durumu Mükerrer mi" kontrolünü
    // sunucu kendi kayıt kümesinden yapar.
    DeleteDuplicateRequests: async function (input) {
        return jsonRequest({
            endpoint: `/api/ReportBilling/DeleteDuplicateRequests`,
            method: 'POST',
            body: input
        })
    },
    UploadReconciliationFile: async function (file, onProgress) {
        const form = new FormData();
        form.append('file', file);
        return uploadRequest({ endpoint: `/api/ReportBilling/UploadReconciliationFile`, form, onProgress })
    },
    Reconcile: async function (input) {
        return jsonRequest({
            endpoint: `/api/ReportBilling/Reconcile`,
            method: 'POST',
            body: input
        })
    },
    // Kovalar sunucuda saklanmaz: aynı girdiyle yeniden türetilir, böylece uygulama anındaki
    // durum baz alınır ve arada değişen kayıt kendiliğinden başka kovaya düşer.
    ApplyReconciliation: async function (input) {
        return jsonRequest({
            endpoint: `/api/ReportBilling/ApplyReconciliation`,
            method: 'POST',
            body: input
        })
    },
    ExportNotFound: async function (input) {
        return jsonRequest({
            endpoint: `/api/ReportBilling/ExportNotFound`,
            method: 'POST',
            body: input,
            isResponseFile: true
        })
    },
}

const ReportTemplate = {
    GetListByRequestType: async function (reqTypeId) {
        return jsonRequest({
            endpoint: `/api/ReportTemplate/GetListByRequestType/${reqTypeId}`,
            method: 'GET'
        })
    },
    SaveReportTemplateSort: async function (body) {
        return jsonRequest({
            endpoint: `/api/ReportTemplate/SaveReportTemplateSort`,
            method: 'POST',
            body
        })
    },
    GetList: async function (studyId, requestId) {
        return jsonRequest({
            endpoint: `/api/ReportTemplate/GetList/${studyId}/${requestId}`,
            method: 'GET'
        })
    },
    GetDataGrid: async function (gridInput) {
        return jsonRequest({
            endpoint: `/api/ReportTemplate/GetDataGrid`,
            method: 'POST',
            body: gridInput
        })
    },
    Get: async function (templateId) {
        return jsonRequest({
            endpoint: `/api/ReportTemplate/Get/${templateId}`,
            method: 'GET'
        })
    },
    Create: async function (data) {
        return jsonRequest({
            endpoint: `/api/ReportTemplate/Create`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    Edit: async function (templateId, data) {
        return jsonRequest({
            endpoint: `/api/ReportTemplate/Edit/${templateId}`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    DeleteTemplate: async function (templateId) {
        return jsonRequest({
            endpoint: `/api/ReportTemplate/DeleteTemplate/${templateId}`,
            method: 'DELETE'
        })
    },
    RestoreTemplate: async function (templateId) {
        return jsonRequest({
            endpoint: `/api/ReportTemplate/RestoreTemplate/${templateId}`,
            method: 'POST'
        })
    },
    GetReportTemplateFileData: function (templateId) {
        return jsonRequest({
            endpoint: `/api/ReportTemplate/GetReportTemplateFileData/${templateId}`,
            method: 'GET',
        })
    }
}

const ReportAiTemplate = {
    Create: async function (data) {
        return jsonRequest({
            endpoint: `/api/ReportAi/CreateAiTemplate`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    GetByRequestType: async function (requestTypeId) {
        return jsonRequest({
            endpoint: `/api/ReportAi/GetAiTemplatesByRequestType`,
            method: 'POST',
            body: requestTypeId
        })
    },
    GetDataGrid: async function (queryModel) {
        return jsonRequest({
            endpoint: `/api/ReportAi/GetDataGrid`,
            method: 'POST',
            body: queryModel
        })
    },
    Get: async function (id) {
        return jsonRequest({
            endpoint: `/api/ReportAi/GetAiTemplate/${id}`,
            method: 'GET'
        })
    },
    GetFileData: async function (id) {
        return jsonRequest({
            endpoint: `/api/ReportAi/GetAiTemplateFileData/${id}`,
            method: 'GET'
        })
    },
    Edit: async function (id, data) {
        return jsonRequest({
            endpoint: `/api/ReportAi/EditAiTemplate/${id}`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    Delete: async function (id) {
        return jsonRequest({
            endpoint: `/api/ReportAi/DeleteAiTemplate/${id}`,
            method: 'POST'
        })
    },
    GenerateAiReport: async function (studyId) {
        return jsonRequest({
            endpoint: `/api/ReportAi/GenerateAiReport/${studyId}`,
            method: 'POST'
        })
    }
}

const Request = {
    SetRequests: async function (studyId, input) {
        return jsonRequest({
            endpoint: `/api/Request/SetRequests/${studyId}`,
            method: 'POST',
            body: input,
            showSuccess: true
        })
    },
    AddRequests: async function (studyId, input) {
        return jsonRequest({
            endpoint: `/api/Request/AddRequests/${studyId}`,
            method: 'POST',
            body: input,
            showSuccess: true
        })
    },
    Get: async function (studyId, requestId) {
        return jsonRequest({
            endpoint: `/api/Request/Get/${studyId}/${requestId}`,
            method: 'GET'
        })
    },
    DeleteRequest: async function (studyId, requestId) {
        return jsonRequest({
            endpoint: `/api/Request/DeleteRequest/${studyId}/${requestId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    RestoreRequest: async function (studyId, requestId) {
        return jsonRequest({
            endpoint: `/api/Request/RestoreRequest/${studyId}/${requestId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    // silent: çağıran kendi göstergesini yönetiyor (grid tablonun içinde gösteriyor) — bkz. Study.DataGrid.
    GetStudyRequestTableList: async function (studyId, { silent } = {}) {
        return jsonRequest({
            endpoint: `/api/Request/GetStudyRequestTableList/${studyId}`,
            method: 'GET',
            hideLoader: silent
        })
    },
    GetOtherRequestTableList: async function (studyId, { silent } = {}) {
        return jsonRequest({
            endpoint: `/api/Request/GetOtherRequestTableList/${studyId}`,
            method: 'GET',
            hideLoader: silent
        })
    },
    GetTargetStudiesForMoveRequest: async function (studyId) {
        return jsonRequest({
            endpoint: `/api/Request/GetTargetStudiesForMoveRequest/${studyId}`,
            method: 'GET',
        })
    },
    MoveRequest: async function (studyId, input) {
        return jsonRequest({
            endpoint: `/api/Request/MoveRequest/${studyId}`,
            method: 'POST',
            body: input,
            showSuccess: true
        })
    },
}

const RequestTypeGroup = {
    Create: async function (modalityId, name) {
        return jsonRequest({
            endpoint: `/api/RequestTypeGroup/Create/${modalityId}`,
            method: 'POST',
            body: name
        })
    },
    Edit: async function (modalityId, groupId, name) {
        return jsonRequest({
            endpoint: `/api/RequestTypeGroup/Edit/${modalityId}/${groupId}`,
            method: 'POST',
            body: name
        })
    },
}

const RequestType = {
    Create: async function (modalityId, data) {
        return jsonRequest({
            endpoint: `/api/RequestType/Create/${modalityId}`,
            method: 'POST',
            body: data
        })
    },
    Edit: async function (modalityId, typeId, data) {
        return jsonRequest({
            endpoint: `/api/RequestType/Edit/${modalityId}/${typeId}`,
            method: 'POST',
            body: data
        })
    },
    GetByModality: async function (modalityId) {
        return jsonRequest({
            endpoint: `/api/RequestType/GetByModality/${modalityId}`,
            method: 'GET'
        })
    },
    GetList: async function (modalityId) {
        return jsonRequest({
            endpoint: `/api/RequestType/GetList`,
            method: 'GET'
        })
    },
    GetAvailableRequestTypes: async function (studyId) {
        return jsonRequest({
            endpoint: `/api/RequestType/GetAvailableRequestTypes/${studyId}`,
            method: 'GET'
        })
    },
}

const StudyFile = {
    // silent: bkz. Study.DataGrid — StudyFileGrid göstergeyi tablonun içinde veriyor.
    DataGridByStudyId: function (studyId, gridInput, { silent } = {}) {
        return jsonRequest({
            endpoint: `/api/StudyFile/GetDataGridByStudyId/${studyId}`,
            method: 'POST',
            body: gridInput,
            hideLoader: silent
        })
    },
    UploadFileUrl: function (studyId) {
        return `${getApi()}/api/StudyFile/UploadFile/${studyId}`;
    },
    DownloadFile: function (studyId, fileId) {
        return jsonRequest({
            endpoint: `/api/StudyFile/DownloadFile/${studyId}/${fileId}`,
            method: 'GET',
            isResponseFile: true
        })
    },
    DeleteFile: function (studyId, fileId) {
        return jsonRequest({
            endpoint: `/api/StudyFile/DeleteFile/${studyId}/${fileId}`,
            method: 'POST',
        })
    },
    RestoreFile: function (studyId, fileId) {
        return jsonRequest({
            endpoint: `/api/StudyFile/RestoreFile/${studyId}/${fileId}`,
            method: 'POST',
        })
    }
}

const Study = {
    // silent: çağıran kendi yükleme göstergesini yönetiyor (StudyGrid tablonun içinde gösteriyor) —
    // küresel gösterge susar, iki ayrı gösterge aynı anda yanmaz.
    DataGrid: function (searchInput, { silent } = {}) {
        return jsonRequest({
            endpoint: `/api/Study/GetDataGrid`,
            method: 'POST',
            body: searchInput,
            hideLoader: silent
        })
    },
    // Sayaç ayrı istek: grid cevabı sayım beklemez. exact=false → sunucu 500'de keser ({count, isCapped}).
    // hideLoader: sayım sayfayı bloklamamalı — geri bildirim grid'in toplam alanındaki yerel spinner ile verilir.
    DataGridCount: function (searchInput, exact = false) {
        return jsonRequest({
            endpoint: `/api/Study/GetDataGridCount?exact=${exact}`,
            method: 'POST',
            body: searchInput,
            hideLoader: true
        })
    },
    Get: async function (studyId) {
        return jsonRequest({
            endpoint: `/api/Study/Get/${studyId}`,
            method: 'GET',
        })
    },
    // silent: bkz. Study.DataGrid — StudyLogsGrid göstergeyi tablonun içinde veriyor.
    GetStudyLogs: async function (gridInput, { silent } = {}) {
        return jsonRequest({
            endpoint: `/api/Study/GetStudyLogs`,
            method: 'POST',
            body: gridInput,
            hideLoader: silent
        })
    },
    DeleteStudies: async function (studyIdList) {
        return jsonRequest({
            endpoint: `/api/Study/DeleteStudy`,
            method: 'POST',
            body: studyIdList
        })
    },
    DeleteStudiesPermanent: async function (studyIdList) {
        return jsonRequest({
            endpoint: `/api/Study/DeleteStudiesPermanent`,
            method: 'DELETE',
            body: studyIdList
        })
    },
    RestoreStudies: async function (studyIdList) {
        return jsonRequest({
            endpoint: `/api/Study/RestoreStudy`,
            method: 'POST',
            body: studyIdList
        })
    },
    EditInstitutions: async function (studyId, data) {
        return jsonRequest({
            endpoint: `/api/Study/EditInstitutions/${studyId}`,
            method: 'POST',
            body: data
        })
    },
    UpdateStudyMetadata: async function (data) {
        return jsonRequest({
            endpoint: `/api/Study/UpdateStudyMetadata`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    GetInstitutions: async function () {
        return jsonRequest({
            endpoint: `/api/Study/GetInstitutions`,
            method: 'GET',
        })
    },
    // Onay ekranının önce/sonra özeti. Hiçbir şey değiştirmez; kapsam (AllStudies dahil) sunucuda
    // birleştirmenin kendisiyle aynı kodla hesaplanır.
    PreviewMergeStudiesToPatient: async function (data) {
        return jsonRequest({
            endpoint: `/api/Study/PreviewMergeStudiesToPatient`,
            method: 'POST',
            body: data
        })
    },
    // hideLoader: işlem dakikalar sürebiliyor; tam ekran spinner yerine modaldeki canlı ilerleme gösterilir.
    MergeStudiesToPatient: async function (data) {
        return jsonRequest({
            endpoint: `/api/Study/MergeStudiesToPatient`,
            method: 'POST',
            body: data,
            showSuccess: true,
            hideLoader: true
        })
    },
}

const Weasis = {
    GetInstallers: () => {
        return jsonRequest({
            endpoint: `/api/Weasis/GetInstallers`,
            method: 'GET',
            hideLoader: true
        })
    },
    CreateWeasisUri: (studies) => {
        return jsonRequest({
            endpoint: `/api/Weasis/CreateWeasisUri`,
            method: 'POST',
            body: studies
        })
    },
    CreateCDUri: (studies) => {
        return jsonRequest({
            endpoint: `/api/Weasis/CreateCDUri`,
            method: 'POST',
            body: studies
        })
    },
}

const User = {
    Information: async function () {
        return jsonRequest({
            endpoint: `/api/User/Information`,
            method: 'GET'
        })
    },
    GetConfig: async function () {
        return jsonRequest({
            endpoint: `/api/User/GetConfig`,
            method: 'GET'
        })
    },
    GetUserDashboards: async function () {
        return jsonRequest({
            endpoint: `/api/User/GetUserDashboards`,
            method: 'GET'
        })
    },
    GetDashboardLink: async function (id) {
        return jsonRequest({
            endpoint: `/api/User/GetDashboardLink/${id}`,
            method: 'GET'
        })
    },
    UpdateUserDictionary: async function (dictionary) {
        return jsonRequest({
            endpoint: `/api/User/UpdateUserDictionary`,
            method: 'POST',
            body: dictionary,
            showSuccess: true
        })
    },
    // Yanıttaki access_token, parola değişimiyle geçersizleşen eski token'ın yerine saklanmalı.
    ChangePassword: async function (body) {
        return jsonRequest({
            endpoint: `/api/User/ChangePassword`,
            method: 'POST',
            body: body
        })
    }
}

const VoiceRecord = {
    // silent: bkz. Study.DataGrid — VoiceRecordGrid göstergeyi tablonun içinde veriyor.
    GetList: async function (studyId, { silent } = {}) {
        return jsonRequest({
            endpoint: `/api/VoiceRecord/GetList/${studyId}`,
            method: 'GET',
            hideLoader: silent
        })
    },
    GetOtherVoiceRecords: async function (studyId, { silent } = {}) {
        return jsonRequest({
            endpoint: `/api/VoiceRecord/GetOtherVoiceRecords/${studyId}`,
            method: 'GET',
            hideLoader: silent
        })
    },
    DeleteVoiceRecord: async function (studyId, recordId) {
        return jsonRequest({
            endpoint: `/api/VoiceRecord/DeleteVoiceRecord/${studyId}/${recordId}`,
            method: 'POST',
        })
    },
    RestoreVoiceRecord: async function (studyId, recordId) {
        return jsonRequest({
            endpoint: `/api/VoiceRecord/RestoreVoiceRecord/${studyId}/${recordId}`,
            method: 'POST',
        })
    },
    CreateZip: async function (studyIdList) {
        return jsonRequest({
            endpoint: `/api/VoiceRecord/CreateZip`,
            method: 'POST',
            isResponseFile: true,
            body: studyIdList,
            showSuccess: true
        })
    },
    GetFile: async function (studyId, recordId) {
        return jsonRequest({
            endpoint: `/api/VoiceRecord/GetFile/${studyId}/${recordId}`,
            method: 'GET',
            isResponseFile: true
        })
    },
    UploadBase64: async function (studyId, data) {
        return jsonRequest({
            endpoint: `/api/VoiceRecord/UploadBase64/${studyId}`,
            method: 'POST',
            body: data
        })
    },
    AddVoiceRecords: async function (studyId, data) {
        return jsonRequest({
            endpoint: `/api/VoiceRecord/AddVoiceRecords/${studyId}`,
            method: 'POST',
            body: data
        })
    }
}

const StudyAnamnez = {
    GetStudyAnamnez: function (studyId) {
        return jsonRequest({
            endpoint: `/api/StudyAnamnez/GetStudyAnamnez/${studyId}`,
            method: `GET`
        })
    },
    EditStudyAnamnez: function (studyId, data) {
        return jsonRequest({
            endpoint: `/api/StudyAnamnez/EditStudyAnamnez/${studyId}`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
    EditClinicalInformation: function (studyId, data) {
        return jsonRequest({
            endpoint: `/api/StudyAnamnez/EditClinicalInformation/${studyId}`,
            method: `POST`,
            body: data,
            showSuccess: true
        })
    },
}

const StudyDetails = {
    AddRead: function (data) {
        return jsonRequest({
            endpoint: `/api/StudyDetail/AddRead`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    DeleteReadUser: function (studyIdList, userId) {
        if (!userId)
            return jsonRequest({
                endpoint: `/api/StudyDetail/RemoveRead`,
                method: 'DELETE',
                body: studyIdList,
                showSuccess: true
            })

        return jsonRequest({
            endpoint: `/api/StudyDetail/DeleteReadUser/${userId}`,
            method: 'DELETE',
            body: studyIdList,
            showSuccess: true
        })
    },
    RemoveRead: function (studyIdList) {
        return jsonRequest({
            endpoint: `/api/StudyDetail/RemoveRead`,
            method: 'DELETE',
            body: studyIdList,
            showSuccess: true
        })
    },
    SetStudyType: function (data) {
        return jsonRequest({
            endpoint: `/api/StudyDetail/SetStudyType`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    GetReadUsersOfStudy: function (studyIdList) {
        return jsonRequest({
            endpoint: `/api/StudyDetail/GetReadUsersOfStudy`,
            method: 'POST',
            body: studyIdList
        })
    },
    UpdateStudyStatus: function (data) {
        return jsonRequest({
            endpoint: `/api/StudyDetail/UpdateStudyStatus`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    GetStudyStatusHistory: function (gridInput) {
        return jsonRequest({
            endpoint: `/api/StudyDetail/GetStudyStatusHistory`,
            method: 'POST',
            body: gridInput
        })
    },
    SetReportable: function (data) {
        return jsonRequest({
            endpoint: `/api/StudyDetail/SetReportable`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    }
}

const ReportOutputTemplate = {
    UploadReportOutputTemplate: function (hospitalId, data) {
        return jsonRequest({
            endpoint: `/api/ReportOutputTemplate/UploadReportOutputTemplate/${hospitalId}`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    DownloadReportOutputTemplate: function (id, hospitalId) {
        return jsonRequest({
            endpoint: `/api/ReportOutputTemplate/DownloadReportOutputTemplate/${hospitalId}/${id}`,
            method: 'GET',
            isResponseFile: true
        })
    },

    // ---- builder (v2) ----
    ListTemplates: function (hospitalId) {
        return jsonRequest({
            endpoint: `/api/ReportOutputTemplate/ListTemplates/${hospitalId}`,
            method: 'GET'
        })
    },
    GetDefinition: function (hospitalId, templateId) {
        return jsonRequest({
            endpoint: `/api/ReportOutputTemplate/GetDefinition/${hospitalId}/${templateId}`,
            method: 'GET'
        })
    },
    SaveDefinition: function (hospitalId, data) {
        return jsonRequest({
            endpoint: `/api/ReportOutputTemplate/SaveDefinition/${hospitalId}`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    PreviewDefinition: function (hospitalId, data) {
        return jsonRequest({
            endpoint: `/api/ReportOutputTemplate/PreviewDefinition/${hospitalId}`,
            method: 'POST',
            body: data
        })
    },
    Placeholders: function () {
        return jsonRequest({
            endpoint: `/api/ReportOutputTemplate/Placeholders`,
            method: 'GET'
        })
    },
    Revisions: function (hospitalId, templateId) {
        return jsonRequest({
            endpoint: `/api/ReportOutputTemplate/Revisions/${hospitalId}/${templateId}`,
            method: 'GET'
        })
    },
    RestoreRevision: function (hospitalId, revisionId) {
        return jsonRequest({
            endpoint: `/api/ReportOutputTemplate/RestoreRevision/${hospitalId}/${revisionId}`,
            method: 'POST',
            showSuccess: true
        })
    },
}

const Dashboard = {
    HospitalStatistics: function (data) {
        return jsonRequest({
            endpoint: `/api/Dashboard/HospitalStatistics`,
            method: 'POST',
            body: data
        })
    },
    ReadUserStatistics: function (data) {
        return jsonRequest({
            endpoint: `/api/Dashboard/ReadUserStatistics`,
            method: 'POST',
            body: data
        })
    },
    WrittenUserStatistics: function (data) {
        return jsonRequest({
            endpoint: `/api/Dashboard/WrittenUserStatistics`,
            method: 'POST',
            body: data
        })
    }
}

function downloadFile(url, fileName) {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    fileName && link.setAttribute('download', fileName);
    link.click();
}

const StudyDashboard = {
    ByHospital: function (data) {
        return jsonRequest({
            endpoint: `/api/StudyDashboard/GetDashboardByHospital`,
            method: 'POST',
            body: data
        })
    },
    ByHospitalModality: function (data) {
        return jsonRequest({
            endpoint: `/api/StudyDashboard/GetDashboardByHospitalModality`,
            method: 'POST',
            body: data
        })
    },
    ByHospitalModalityUser: function (data) {
        return jsonRequest({
            endpoint: `/api/StudyDashboard/GetDashboardByHospitalModalityUser`,
            method: 'POST',
            body: data
        })
    },
    ByHospitalModalityCurrentUser: function (data) {
        return jsonRequest({
            endpoint: `/api/StudyDashboard/GetDashboardByHospitalModalityCurrentUser`,
            method: 'POST',
            body: data
        })
    },
}

const Dicom = {
    DownloadDicomZip: async (studyIdList) => {
        const urls = studyIdList.map(item => `${window.AppSettings.server.api}/api/Dicom/GetStudyZip/${item.studyId}/true/true${tokenQueryParam('?')}`)

        console.log(urls)
        for (let i = 0; i < urls.length; i++) {
            setTimeout(() => {
                const link = document.createElement('a');
                link.href = urls[i];
                link.setAttribute('download', '');
                link.click()

            }, 5000 * i)
        }
    },
    UploadDicom: (data) => {
        return jsonRequest({
            endpoint: `/api/Dicom/UploadDicom`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    GetStudyDicomMetadata: (studies) => {
        return `${getApi()}/api/Dicom/GetStudyDicomMetadata/${studies}${tokenQueryParam('?')}`
    },
    // silent: bkz. Study.DataGrid — SeriesMetadataTable göstergeyi tablonun içinde veriyor.
    GetSeriesMetadata: (studyId, { silent } = {}) => {
        return jsonRequest({
            endpoint: `/api/Dicom/GetSeriesMetadata/${studyId}`,
            method: 'GET',
            showSuccess: false,
            hideLoader: silent
        })
    },
    GetDicomFile: (studyId, studyUid, seriesUid, instanceUid) => {
        return jsonRequest({
            endpoint: `/api/Dicom/GetDicomFile/${studyId}/${studyUid}/${seriesUid}/${instanceUid}`,
            method: 'GET',
            showSuccess: false,
            isResponseFile: true,
            hideLoader: true,
            hideError: true
        })
    },
    GetExportAETitleList: (data) => {
        return jsonRequest({
            endpoint: `/api/Dicom/GetExportAETitleList`,
            method: 'POST',
            showSuccess: false,
            body: data
        })
    },
    ExportStudies: (exportName, data) => {
        return jsonRequest({
            endpoint: `/api/Dicom/ExportStudies/${exportName}`,
            method: 'POST',
            showSuccess: true,
            body: data
        })
    },
}

const StudyUser = {
    AssignDoctorUser: (data) => {
        return jsonRequest({
            endpoint: `/api/StudyUser/AssignDoctorUser`,
            method: 'POST',
            body: data,
            showSuccess: true,
        });
    },
    RemoveDoctorUser: (data) => {
        return jsonRequest({
            endpoint: `/api/StudyUser/RemoveDoctorUser`,
            method: 'POST',
            body: data,
            showSuccess: true,
        });
    },
    AssignReporterUser: (data) => {
        return jsonRequest({
            endpoint: `/api/StudyUser/AssignReporterUser`,
            method: 'POST',
            body: data,
            showSuccess: true,
        });
    },
    RemoveReporterUser: (data) => {
        return jsonRequest({
            endpoint: `/api/StudyUser/RemoveReporterUser`,
            method: 'POST',
            body: data,
            showSuccess: true,
        });
    },
};

const Auth = {
    Login: (data) => {
        return jsonRequest({
            endpoint: `/api/Auth/Login`,
            method: 'POST',
            body: {
                ...data,
                grant_type: 'password'
            },
            showSuccess: true
        })
    },
    SmsVerify: (data) => {
        return jsonRequest({
            endpoint: `/api/Auth/SmsVerify`,
            method: 'POST',
            body: {
                ...data,
                grant_type: 'sms'
            },
            showSuccess: true
        })
    },
    SendCode: (data) => {
        return jsonRequest({
            endpoint: `/api/Auth/SendCode`,
            method: 'POST',
            body: {
                ...data,
                grant_type: 'sms'
            },
            showSuccess: true
        })
    },

    // Elde kalan eski token'ı çerezli oturuma taşır (güncelleme sonrası tek seferlik).
    // Hata gösterilmez: başarısız olursa kullanıcı token'lı yoldan çalışmaya devam eder.
    Session: () => {
        return jsonRequest({
            endpoint: `/api/Auth/Session`,
            method: 'POST',
            hideLoader: true,
            hideError: true
        })
    },
    Logout: () => {
        return jsonRequest({
            endpoint: `/api/Auth/Logout`,
            method: 'POST',
            hideLoader: true,
            hideError: true
        })
    },

    // Parola sıfırlama: ForgotPassword → SendResetCode → ResetPassword.
    // grant_type GÖNDERİLMEZ; akışı challenge'ın içindeki imzalı amaç (purpose) belirler.
    ForgotPassword: (data) => {
        return jsonRequest({
            endpoint: `/api/Auth/ForgotPassword`,
            method: 'POST',
            body: data,
        })
    },
    SendResetCode: (data) => {
        return jsonRequest({
            endpoint: `/api/Auth/SendResetCode`,
            method: 'POST',
            body: data,
        })
    },
    ResetPassword: (data) => {
        return jsonRequest({
            endpoint: `/api/Auth/ResetPassword`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
}

const UserManager = {
    CreateUser: async function (data) {
        return jsonRequest({
            endpoint: `/api/UserManager/CreateUser`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    EditUser: async function (userId, data) {
        return jsonRequest({
            endpoint: `/api/UserManager/EditUser/${userId}`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    SetHospitals: async function (userId, data) {
        return jsonRequest({
            endpoint: `/api/UserManager/SetHospitals/${userId}`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    // hideLoader: çevrimiçi listesinin arka planda tazelenmesi için — yükleme göstergesi olmadan.
    GetUserDataGrid: async function (gridInput, hideLoader = false) {
        return jsonRequest({
            endpoint: `/api/UserManager/GetUserDataGrid`,
            method: 'POST',
            body: gridInput,
            hideLoader
        })
    },
    GetUserList: async function () {
        return jsonRequest({
            endpoint: `/api/UserManager/GetUserList`,
            method: 'GET',
        })
    },
    GetUser: async function (userId) {
        return jsonRequest({
            endpoint: `/api/UserManager/GetUser/${userId}`,
            method: 'GET'
        })
    },
    GetDoctorSignImage: async function (userId) {
        return jsonRequest({
            endpoint: `/api/UserManager/GetDoctorSignImage/${userId}`,
            method: 'GET',
            isResponseFile: true
        })
    },
    GetHospitalsOfUser: async function () {
        return jsonRequest({
            endpoint: `/api/UserManager/GetHospitalsOfUser`,
            method: 'GET'
        })
    },
    GetInstitutionsOfUser: async function () {
        return jsonRequest({
            endpoint: `/api/UserManager/GetInstitutionsOfUser`,
            method: 'GET'
        })
    },
    GetReadableUsers: async function () {
        return jsonRequest({
            endpoint: `/api/UserManager/GetReadableUsers`,
            method: 'GET'
        })
    },
    GetWritableUsers: async function () {
        return jsonRequest({
            endpoint: `/api/UserManager/GetWritableUsers`,
            method: 'GET'
        })
    },
    SetUsersToHospital: async function (data) {
        return jsonRequest({
            endpoint: `/api/UserManager/SetUsersToHospital`,
            method: 'POST',
            body: data
        })
    },
    GetUsersOfHospital: async function (hospitalId) {
        return jsonRequest({
            endpoint: `/api/UserManager/GetUsersOfHospital/${hospitalId}`,
            method: 'GET'
        })
    },
    GetDoctorsOfHospital: async function (hospitalId) {
        return jsonRequest({
            endpoint: `/api/UserManager/GetDoctorsOfHospital/${hospitalId}`,
            method: 'GET',
        })
    },
    SetDoctorsToHospital: async function (hospitalId, data) {
        return jsonRequest({
            endpoint: `/api/UserManager/SetDoctorsToHospital/${hospitalId}`,
            method: 'POST',
            body: data
        })
    },
    SetUsersToDevice: async function (data) {
        return jsonRequest({
            endpoint: `/api/UserManager/SetUsersToDevice`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    GetUsersOfDevice: async function (deviceId, hospitalId) {
        return jsonRequest({
            endpoint: `/api/UserManager/GetUsersOfDevice/${deviceId}/${hospitalId}`,
            method: 'GET'
        })
    },
}

const HospitalGroup = {
    Create: async function (data) {
        return jsonRequest({
            endpoint: `/api/HospitalGroup/Create`,
            method: 'POST',
            body: data
        })
    },
    Edit: async function (groupId, data) {
        return jsonRequest({
            endpoint: `/api/HospitalGroup/Edit/${groupId}`,
            method: 'POST',
            body: data
        })
    },
    Delete: async function (groupId) {
        return jsonRequest({
            endpoint: `/api/HospitalGroup/Delete/${groupId}`,
            method: 'DELETE',
        })
    },
    GetById: async function (groupId) {
        return jsonRequest({
            endpoint: `/api/HospitalGroup/GetById/${groupId}`,
            method: 'GET',
        })
    },
    GetList: async function () {
        return jsonRequest({
            endpoint: `/api/HospitalGroup/GetList`,
            method: 'GET',
        })
    },
    SetHospitalsToGroup: async function (data) {
        return jsonRequest({
            endpoint: `/api/HospitalGroup/SetHospitalsToGroup`,
            method: 'POST',
            body: data
        })
    },
}

const HL7Client = {
    CreateClient: async function (data) {
        return jsonRequest({
            endpoint: `/api/HL7Client/CreateClient`,
            method: 'POST',
            body: data
        })
    },
    EditClient: async function (id, data) {
        return jsonRequest({
            endpoint: `/api/HL7Client/EditClient/${id}`,
            method: 'PUT',
            body: data
        })
    },
    RefreshToken: async function (id) {
        return jsonRequest({
            endpoint: `/api/HL7Client/RefreshToken/${id}`,
            method: 'PUT',
        })
    },
    DeleteClient: async function (id) {
        return jsonRequest({
            endpoint: `/api/HL7Client/DeleteClient/${id}`,
            method: 'DELETE',
        })
    },
    GetHL7ClientDataGrid: async function (hospitalId) {
        return jsonRequest({
            endpoint: `/api/HL7Client/GetHL7ClientList/${hospitalId}`,
            method: 'GET',
        })
    },
}

const HL7Service = {
    GetDataGrid: async (gridInput) => {
        return jsonRequest({
            endpoint: `/api/HL7/GetDataGrid`,
            method: 'POST',
            body: gridInput
        })
    },
    // Sayaç ayrı istek: grid cevabı sayım beklemez. exact=false → sunucu 500'de keser ({count, isCapped}).
    // hideLoader: sayım sayfayı bloklamamalı — geri bildirim grid'in toplam alanındaki yerel spinner ile verilir.
    GetDataGridCount: async (gridInput, exact = false) => {
        return jsonRequest({
            endpoint: `/api/HL7/GetDataGridCount?exact=${exact}`,
            method: 'POST',
            body: gridInput,
            hideLoader: true
        })
    },
    GetHL7ClientDataGrid: async function (messageId) {
        return jsonRequest({
            endpoint: `/api/HL7/GetHL7FileMessage/${messageId}`,
            method: 'GET',
        })
    },
    GetHL7Message: async function (messageId) {
        return jsonRequest({
            endpoint: `/api/HL7/GetHL7FileMessage/${messageId}`,
            method: 'GET',
        })
    },
    CreateReportMessage: async function (reportIdList) {
        return jsonRequest({
            endpoint: `/api/HL7/CreateReportMessage`,
            method: 'POST',
            body: reportIdList
        })
    },
    SendReport: async function (messageIdList) {
        return jsonRequest({
            endpoint: `/api/HL7/SendReport`,
            method: 'POST',
            body: messageIdList
        })
    },
    TestAckRules: async function (rules, messageIds) {
        return jsonRequest({
            endpoint: `/api/HL7/TestAckRules`,
            method: 'POST',
            body: { rules, messageIds }
        })
    },
    ManualMatchHL7: async function (messageId, studyId) {
        return jsonRequest({
            endpoint: `/api/HL7/ManualMatchHL7/${messageId}/${studyId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    ManualProcess: async function (messageId) {
        return jsonRequest({
            endpoint: `/api/HL7/ManualProcess/${messageId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    AddManualHL7: async function (hospitalId, data) {
        return jsonRequest({
            endpoint: `/api/HL7/AddManualHL7Message/${hospitalId}`,
            method: 'POST',
            showSuccess: true,
            body: data
        })
    },
}

const Notification = {
    GetNotificationList: async function (page) {
        return jsonRequest({
            endpoint: `/api/Notification/GetNotificationList/${page}`,
            method: 'GET',
            hideLoader: true,
            hideError: true
        })
    },
    // Zil rozeti: liste çekmeden okunmamış sayısı.
    GetUnreadCount: async function () {
        return jsonRequest({
            endpoint: `/api/Notification/GetUnreadCount`,
            method: 'GET',
            hideLoader: true,
            hideError: true
        })
    },
    /**
     * Bildirimin kendi taşıdığı indirme adresine gider (FileResult.DownloadUrl).
     * Adres SUNUCU tarafından yazılıyor; yine de istemci yalnız kendi API'sinin yollarına
     * gitsin diye "/api/" ön eki şart koşulur.
     */
    DownloadByUrl: async function (url, notificationId) {
        if (typeof url !== 'string' || !url.startsWith('/api/'))
            return { success: false }

        return jsonRequest({
            endpoint: `${url}/${notificationId}`,
            method: 'POST',
            isResponseFile: true
        })
    },
    SetRead: async function (id) {
        return jsonRequest({
            endpoint: `/api/Notification/SetRead/${id}`,
            method: 'PUT',
            hideLoader: true,
            hideError: true
        })
    },
    RemoveItem: async function (id) {
        return jsonRequest({
            endpoint: `/api/Notification/RemoveItem/${id}`,
            method: 'DELETE',
            hideLoader: true,
            hideError: true
        })
    },
    Clear: async function () {
        return jsonRequest({
            endpoint: `/api/Notification/Clear`,
            method: 'DELETE',
            hideLoader: true,
            hideError: true
        })
    },

    // --- yönetici paneli (NOTIFICATION_MANAGE) ---
    Send: async function (input) {
        return jsonRequest({
            endpoint: `/api/Notification/Send`,
            method: 'POST',
            body: input
        })
    },
    GetOnlineUsers: async function () {
        return jsonRequest({
            endpoint: `/api/Notification/GetOnlineUsers`,
            method: 'GET',
            hideLoader: true
        })
    },
    GetConnections: async function () {
        return jsonRequest({
            endpoint: `/api/Notification/GetConnections`,
            method: 'GET',
            hideLoader: true
        })
    },
    GetRecipients: async function () {
        return jsonRequest({
            endpoint: `/api/Notification/GetRecipients`,
            method: 'GET',
            hideLoader: true
        })
    },
    GetSentNotifications: async function (gridInput) {
        return jsonRequest({
            endpoint: `/api/Notification/GetSentNotifications`,
            method: 'POST',
            body: gridInput
        })
    },
}

const Teletip = {
    MakeKos: async (data) => {
        return jsonRequest({
            endpoint: `/api/Teletip/MakeKos`,
            method: 'POST',
            showSuccess: true,
            body: data
        })
    },
    SendKos: async (teletipIdList) => {
        return jsonRequest({
            endpoint: `/api/Teletip/SendKos`,
            method: 'POST',
            body: teletipIdList,
            showSuccess: true,
        })
    },
    GetTeletipGrid: async (gridInput) => {
        return jsonRequest({
            endpoint: `/api/Teletip/GetTeletipGrid`,
            method: 'POST',
            body: gridInput
        })
    },
}

const StudyShare = {
    CreateShareLinks: async (data) => {
        return jsonRequest({
            endpoint: `/api/StudyShare/CreateShareLinks`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    SendShareLinksByEmail: async (data) => {
        return jsonRequest({
            endpoint: `/api/StudyShare/SendShareLinksByEmail`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    GetShareInfo: async (token) => {
        return jsonRequest({
            endpoint: `/api/StudyShare/GetShareInfo/${token}`,
            method: 'GET'
        })
    },
    Download: (token) => {
        return `${getApi()}/api/StudyShare/Download/${token}`
    }
}

const Appointment = {
    Create: async function (data) {
        return jsonRequest({
            endpoint: `/api/Appointment/Create`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    Update: async function (data) {
        return jsonRequest({
            endpoint: `/api/Appointment/Update`,
            method: 'PUT',
            body: data,
            showSuccess: true
        })
    },
    UpdateStatus: async function (data) {
        return jsonRequest({
            endpoint: `/api/Appointment/UpdateStatus`,
            method: 'PUT',
            body: data,
            showSuccess: true
        })
    },
    Delete: async function (appointmentId) {
        return jsonRequest({
            endpoint: `/api/Appointment/Delete/${appointmentId}`,
            method: 'DELETE',
            showSuccess: true
        })
    },
    Cancel: async function (appointmentId) {
        return jsonRequest({
            endpoint: `/api/Appointment/Cancel/${appointmentId}`,
            method: 'POST',
            showSuccess: true
        })
    },
    GetById: async function (appointmentId) {
        return jsonRequest({
            endpoint: `/api/Appointment/Get/${appointmentId}`,
            method: 'GET'
        })
    },
    GetByDevice: async function (deviceId, hideCancelled, startDate, endDate) {
        return jsonRequest({
            endpoint: `/api/Appointment/GetByDevice/${deviceId}?hideCancelled=${hideCancelled}&startDate=${startDate}&endDate=${endDate}`,
            method: 'GET'
        })
    },
    GetScheduledAppointmentsByPatient: async function (patientId) {
        return jsonRequest({
            endpoint: `/api/Appointment/GetScheduledAppointmentsByPatient/${patientId}`,
            method: 'GET'
        })
    },
}

const Patient = {
    DataGrid: async function (gridInput) {
        return jsonRequest({
            endpoint: `/api/Patient/GetDataGrid`,
            method: 'POST',
            body: gridInput
        })
    },
    // Yazdıkça arama: hasta kimliğinde ön ek eşleşmesi. hideLoader → her tuşta tam sayfa loader açılmasın.
    SearchByDicomPatientId: async function (search, take = 10) {
        return jsonRequest({
            endpoint: `/api/Patient/GetDataGrid`,
            method: 'POST',
            body: {
                paginationQuery: {pageNumber: 1, pageSize: take},
                filter: {dicomPatientIdSearch: search},
                orderByItems: []
            },
            hideLoader: true
        })
    },
    Create: async function (data) {
        return jsonRequest({
            endpoint: `/api/Patient/CreatePatient`,
            method: 'POST',
            body: data
        })
    },
    // hideLoader: çekimler tek tek PACS'e kopyalandığı için uzun sürer; ilerleme modalde gösterilir.
    UpdateAndCopyStudies: async function (data) {
        return jsonRequest({
            endpoint: `/api/Patient/UpdatePatientAndCopyStudies`,
            method: 'POST',
            body: data,
            showSuccess: true,
            hideLoader: true
        })
    },
}

const StudyTask = {
    Create: async function (data) {
        return jsonRequest({
            endpoint: `/api/StudyTask/Create`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    // silent: bkz. Study.DataGrid — StudyTasksGrid göstergeyi tablonun içinde veriyor.
    DataGrid: async function (data, { silent } = {}) {
        return jsonRequest({
            endpoint: `/api/StudyTask/DataGrid`,
            method: 'POST',
            body: data,
            hideLoader: silent
        })
    },
    Resolve: async function (data) {
        return jsonRequest({
            endpoint: `/api/StudyTask/Resolve`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    GetHistory: async function (taskId) {
        return jsonRequest({
            endpoint: `/api/StudyTask/GetHistory/${taskId}`,
            method: 'GET'
        })
    },
    // Yeniden değerlendirme talebi modalının açılış değerleri: varsayılan atanacak doktor
    // (raporun okuyanı) + statik gerekçe listesi. Liste sunucudan gelir ki tek kaynak olsun.
    GetReAssessmentDefaults: async function (studyId, requestId) {
        return jsonRequest({
            endpoint: `/api/StudyTask/GetReAssessmentDefaults/${studyId}/${requestId}`,
            method: 'GET',
            hideLoader: true
        })
    },
}

const ReAssessment = {
    GetDataGrid: async function (data) {
        return jsonRequest({
            endpoint: `/api/ReAssessment/GetDataGrid`,
            method: 'POST',
            body: data
        })
    },
    GetDataGridCount: async function (data, exact = false) {
        return jsonRequest({
            endpoint: `/api/ReAssessment/GetDataGridCount?exact=${exact}`,
            method: 'POST',
            body: data,
            hideLoader: true
        })
    },
    GetStatistics: async function (filter) {
        return jsonRequest({
            endpoint: `/api/ReAssessment/GetStatistics`,
            method: 'POST',
            body: filter
        })
    },
}

const StudyList = {
    // Görünür listeler + yönetim bayrakları (rights). Filtre seçici, ekleme ekranı ve
    // "Listelerim" ekranı AYNI ucu kullanır.
    GetList: async function () {
        return jsonRequest({
            endpoint: `/api/StudyList/GetList`,
            method: 'GET',
            hideLoader: true
        })
    },
    Create: async function (data) {
        return jsonRequest({
            endpoint: `/api/StudyList/Create`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    Rename: async function (data) {
        return jsonRequest({
            endpoint: `/api/StudyList/Rename`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    SetPublic: async function (data) {
        return jsonRequest({
            endpoint: `/api/StudyList/SetPublic`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    Delete: async function (data) {
        return jsonRequest({
            endpoint: `/api/StudyList/Delete`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    GetStudyLists: async function (studyId) {
        return jsonRequest({
            endpoint: `/api/StudyList/GetStudyLists/${studyId}`,
            method: 'GET',
            hideLoader: true
        })
    },
    AddItem: async function (data) {
        return jsonRequest({
            endpoint: `/api/StudyList/AddItem`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    RemoveItem: async function (data) {
        return jsonRequest({
            endpoint: `/api/StudyList/RemoveItem`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    UpdateItem: async function (data) {
        return jsonRequest({
            endpoint: `/api/StudyList/UpdateItem`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
}

const StudyNote = {
    GetByStudy: async function (studyId) {
        return jsonRequest({
            endpoint: `/api/StudyNote/GetByStudy/${studyId}`,
            method: 'GET'
        })
    },
    Save: async function (data) {
        return jsonRequest({
            endpoint: `/api/StudyNote/Save`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    Delete: async function (data) {
        return jsonRequest({
            endpoint: `/api/StudyNote/Delete`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
}

const Log = {
    GetDataGrid: async function (gridInput) {
        return jsonRequest({
            endpoint: `/api/Log/GetDataGrid`,
            method: 'POST',
            body: gridInput
        })
    },
    GetCount: async function (filter) {
        return jsonRequest({
            endpoint: `/api/Log/GetCount`,
            method: 'POST',
            body: filter
        })
    },
    GetAppLogPage: async function (input) {
        return jsonRequest({
            endpoint: `/api/Log/GetAppLogPage`,
            method: 'POST',
            body: input
        })
    },
    GetAppLogCount: async function (filter) {
        return jsonRequest({
            endpoint: `/api/Log/GetAppLogCount`,
            method: 'POST',
            body: filter
        })
    },
    GetAppLogSources: async function () {
        return jsonRequest({
            endpoint: `/api/Log/GetAppLogSources`,
            method: 'GET'
        })
    },
    GetAppLogChannels: async function () {
        return jsonRequest({
            endpoint: `/api/Log/GetAppLogChannels`,
            method: 'GET'
        })
    },
    GetAppLogRoutes: async function () {
        return jsonRequest({
            endpoint: `/api/Log/GetAppLogRoutes`,
            method: 'GET'
        })
    },
    GetRouteStats: async function (from, to) {
        const query = [
            from ? `from=${encodeURIComponent(from)}` : null,
            to ? `to=${encodeURIComponent(to)}` : null
        ].filter(Boolean).join('&');

        return jsonRequest({
            endpoint: `/api/Log/GetRouteStats${query ? `?${query}` : ''}`,
            method: 'GET'
        })
    },
    GetOnlineActors: async function (minutes = 5) {
        return jsonRequest({
            endpoint: `/api/Log/GetOnlineActors?minutes=${minutes}`,
            method: 'GET'
        })
    },
    SetUserEnabled: async function (userId, enabled) {
        return jsonRequest({
            endpoint: `/api/Log/SetUserEnabled?userId=${userId}&enabled=${enabled}`,
            method: 'POST',
            showSuccess: true
        })
    },
}

const AutoExport = {
    GetRules: async function (gridInput) {
        return jsonRequest({
            endpoint: `/api/AutoExportRule/GetDataGrid`,
            method: 'POST',
            body: gridInput
        })
    },
    GetRule: async function (id) {
        return jsonRequest({
            endpoint: `/api/AutoExportRule/Get/${id}`,
            method: 'GET'
        })
    },
    CreateRule: async function (data) {
        return jsonRequest({
            endpoint: `/api/AutoExportRule/Create`,
            method: 'POST',
            body: data,
            showSuccess: true
        })
    },
    UpdateRule: async function (id, data) {
        return jsonRequest({
            endpoint: `/api/AutoExportRule/Update/${id}`,
            method: 'PUT',
            body: data,
            showSuccess: true
        })
    },
    DeleteRule: async function (id) {
        return jsonRequest({
            endpoint: `/api/AutoExportRule/Delete/${id}`,
            method: 'DELETE',
            showSuccess: true
        })
    },
    GetAvailableAeTitles: async function () {
        return jsonRequest({
            endpoint: `/api/AutoExportRule/GetAvailableAeTitles`,
            method: 'GET'
        })
    },
    GetFilterMetadata: async function () {
        return jsonRequest({
            endpoint: `/api/AutoExportRule/GetFilterMetadata`,
            method: 'GET'
        })
    },
    TestQuery: async function (sqlQuery) {
        return jsonRequest({
            endpoint: `/api/AutoExportRule/TestQuery`,
            method: 'POST',
            body: { sqlQuery }
        })
    },
    // silent: bkz. Study.DataGrid — StudyAutoExportHistoryGrid göstergeyi tablonun içinde veriyor.
    GetHistory: async function (filter, { silent } = {}) {
        return jsonRequest({
            endpoint: `/api/AutoExportHistory/GetHistory`,
            method: 'POST',
            body: filter,
            hideLoader: silent
        })
    }
}

const ViewerSettings = {
    GetSettings: async function () {
        return jsonRequest({
            endpoint: `/api/ViewerSettings/GetSettings`,
            method: 'GET'
        })
    },
    SetWeasisVersion: async function (input) {
        return jsonRequest({
            endpoint: `/api/ViewerSettings/SetWeasisVersion`,
            method: 'POST',
            body: input,
            showSuccess: true
        })
    },
    SetLauncherVersion: async function (versionNumber) {
        return jsonRequest({
            endpoint: `/api/ViewerSettings/SetLauncherVersion`,
            method: 'POST',
            body: { versionNumber },
            showSuccess: true
        })
    },
    SetJdkVersion: async function (versionNumber) {
        return jsonRequest({
            endpoint: `/api/ViewerSettings/SetJdkVersion`,
            method: 'POST',
            body: { versionNumber },
            showSuccess: true
        })
    },
    UploadWeasis: async function (version, file, onProgress) {
        const form = new FormData();
        form.append('file', file);
        return uploadRequest({ endpoint: `/api/ViewerSettings/UploadWeasis/${version}`, form, onProgress })
    },
    UploadLauncher: async function (version, platform, file, onProgress) {
        const form = new FormData();
        form.append('file', file);
        return uploadRequest({ endpoint: `/api/ViewerSettings/UploadLauncher/${version}/${platform}`, form, onProgress })
    },
    UploadJdk: async function (version, platform, file, onProgress) {
        const form = new FormData();
        form.append('file', file);
        return uploadRequest({ endpoint: `/api/ViewerSettings/UploadJdk/${version}/${platform}`, form, onProgress })
    },
    UploadFFmpeg: async function (platform, file, onProgress) {
        const form = new FormData();
        form.append('file', file);
        return uploadRequest({ endpoint: `/api/ViewerSettings/UploadFFmpeg/${platform}`, form, onProgress })
    },
    UploadInstaller: async function (platform, file, onProgress) {
        const form = new FormData();
        form.append('file', file);
        return uploadRequest({ endpoint: `/api/ViewerSettings/UploadInstaller/${platform}`, form, onProgress })
    },
    FetchJdk: async function (input) {
        return jsonRequest({
            endpoint: `/api/ViewerSettings/FetchJdk`,
            method: 'POST',
            body: input
        })
    },
    GetFetchJob: async function (jobId) {
        return jsonRequest({
            endpoint: `/api/ViewerSettings/GetFetchJob/${jobId}`,
            method: 'GET',
            hideLoader: true
        })
    },
    DeleteArtifact: async function (kind, version, platform) {
        const suffix = platform ? `/${platform}` : '';
        return jsonRequest({
            endpoint: `/api/ViewerSettings/DeleteArtifact/${kind}/${version}${suffix}`,
            method: 'DELETE',
            showSuccess: true
        })
    },
}

// Beyaz etiket marka yönetimi — veriler DB'de (configuration.json değil), görseller bytea olarak saklanır.
const BrandingApi = {
    GetSettings: async function () {
        return jsonRequest({
            endpoint: `/api/Branding/GetSettings`,
            method: 'GET'
        })
    },
    SaveSettings: async function (input) {
        return jsonRequest({
            endpoint: `/api/Branding/SaveSettings`,
            method: 'POST',
            body: input,
            showSuccess: true
        })
    },
    // kind: HeaderLogo | LoginLogo | LoginBackground | Favicon
    UploadAsset: async function (kind, file, onProgress) {
        const form = new FormData();
        form.append('file', file);
        return uploadRequest({ endpoint: `/api/Branding/UploadAsset/${kind}`, form, onProgress })
    },
    RemoveAsset: async function (kind) {
        return jsonRequest({
            endpoint: `/api/Branding/RemoveAsset/${kind}`,
            method: 'DELETE',
            showSuccess: true
        })
    },
}

// Uygulama yapılandırması (appsettings.json) — dosya tek kaynak, bu uçlar onu güvenle düzenler.
const ConfigApi = {
    GetSchema: async function () {
        return jsonRequest({ endpoint: `/api/Config/GetSchema`, method: 'GET' })
    },
    SaveSettings: async function (updates) {
        return jsonRequest({
            endpoint: `/api/Config/SaveSettings`,
            method: 'POST',
            body: updates,
            showSuccess: true
        })
    },
    ListFiles: async function (key) {
        return jsonRequest({ endpoint: `/api/Config/ListFiles?key=${encodeURIComponent(key)}`, method: 'GET' })
    },
    UploadFile: async function (key, file, onProgress) {
        const form = new FormData();
        form.append('file', file);
        return uploadRequest({ endpoint: `/api/Config/UploadFile?key=${encodeURIComponent(key)}`, form, onProgress })
    },
    DownloadFileUrl: function (key, name) {
        const suffix = name ? `&name=${encodeURIComponent(name)}` : '';
        return `${getApi()}/api/Config/DownloadFile?key=${encodeURIComponent(key)}${suffix}${tokenQueryParam()}`
    },
    DeleteFile: async function (key, name) {
        return jsonRequest({
            endpoint: `/api/Config/DeleteFile?key=${encodeURIComponent(key)}&name=${encodeURIComponent(name)}`,
            method: 'DELETE',
            showSuccess: true
        })
    },
}

const ApiService = {
    setLogout,
    Branding: BrandingApi,
    Config: ConfigApi,
    downloadFile,
    getAuthorization,
    authHeaders,
    tokenQueryParam,
    Auth,
    Hospital,
    PacsSystem,
    Firm,
    Modality,
    Device,
    WorklistItem,
    Appointment,
    Report,
    ReportBilling,
    ReportGenerator,
    ReportTemplate,
    Request,
    RequestType,
    RequestTypeGroup,
    Study,
    Dicom,
    StudyFile,
    Weasis,
    User,
    VoiceRecord,
    StudyAnamnez,
    StudyDetails,
    ReportOutputTemplate,
    Dashboard,
    StudyDashboard,
    Role,
    PermissionGroup,
    UserManager,
    StudyUser,
    HospitalGroup,
    HL7Client,
    HL7Service,
    Notification,
    Teletip,
    StudyShare,
    Patient,
    StudyTask,
    ReAssessment,
    StudyList,
    StudyNote,
    Log,
    AutoExport,
    ReportAiTemplate,
    ViewerSettings
}

export default ApiService;
